import { Box, Table, TableContainer, TableRow } from "@mui/material";
import { Coverage, ProductClasType, ProductNameType } from "@repo/api";
import { isNil } from "lodash";
import { FC } from "react";
import { Icons } from "..";
import { formatDate } from "../../helpers";
import { useTypedTranslation } from "../../hooks";
import { HeadingM } from "../Typography";
import {
    formatFufiCoverageInsuredAmount,
    formatFufiCoveragePremium,
    useFufiCoverageNameFormatter,
    useFufiCoverageStartDateFormatter,
} from "./InsuranceCoverage.helpers";
import * as Styled from "./InsuranceCoverageAccordion.styled";

type InsuranceCoverageDetailProps = {
    coverage: Coverage;
    handleShowTooltip: (title: string, text: string) => void;
    productClass?: ProductClasType;
    insuranceTechnicalStartDate?: Date;
    productName?: ProductNameType | null;
};

export const InsuranceCoverageDetail: FC<InsuranceCoverageDetailProps> = ({
    coverage,
    handleShowTooltip,
    productClass,
    insuranceTechnicalStartDate,
    productName,
}) => {
    const { t } = useTypedTranslation();
    const coverageNameFormatter = useFufiCoverageNameFormatter();
    const coverageStartDateFormatter = useFufiCoverageStartDateFormatter();
    const insuredAmountValue = formatFufiCoverageInsuredAmount(
        coverage,
        productClass,
        t("global.emptyValue"),
        productName,
    );
    const premiumDisplay = formatFufiCoveragePremium(coverage, t("global.emptyValue"), productName);

    return (
        <Box>
            <HeadingM>{coverageNameFormatter(coverage, undefined, productClass, productName)}</HeadingM>
            <TableContainer>
                <Table>
                    <Styled.InsuranceCoverageMobileTableBody>
                        <TableRow>
                            <Styled.NarrowCell>{t("life.contractDetail.coverags.table.tarif")}</Styled.NarrowCell>
                            <Styled.NarrowCell>{coverage.code ?? t("global.emptyValue")}</Styled.NarrowCell>
                        </TableRow>
                        <TableRow>
                            <Styled.NarrowCell>{t("life.contractDetail.coverags.table.stardDate")}</Styled.NarrowCell>
                            <Styled.NarrowCell>
                                {coverageStartDateFormatter(coverage.dateStart, insuranceTechnicalStartDate)}
                            </Styled.NarrowCell>
                        </TableRow>
                        <TableRow>
                            <Styled.NarrowCell>{t("life.contractDetail.coverags.table.endDate")}</Styled.NarrowCell>
                            <Styled.NarrowCell>
                                {formatDate(coverage.dateEnd, t("global.emptyValue"))}
                            </Styled.NarrowCell>
                        </TableRow>
                        <TableRow>
                            <Styled.NarrowCell>
                                {t("life.contractDetail.coverags.table.premium")}
                                {productClass === ProductClasType.Ul && (
                                    <Styled.InfoListItemButton
                                        disableRipple
                                        onClick={() =>
                                            handleShowTooltip(
                                                t("life.contractDetail.coverags.table.premium"),
                                                t("life.contractDetail.coverags.table.premiumTooltip"),
                                            )
                                        }
                                    >
                                        <Icons.Info />
                                    </Styled.InfoListItemButton>
                                )}
                            </Styled.NarrowCell>
                            <Styled.NarrowCell>
                                {premiumDisplay}
                            </Styled.NarrowCell>
                        </TableRow>
                        <TableRow>
                            <Styled.NarrowCell>
                                {t("life.contractDetail.coverags.table.insuredAmount")}
                            </Styled.NarrowCell>
                            <Styled.NarrowCell>{insuredAmountValue}</Styled.NarrowCell>
                        </TableRow>
                        <TableRow>
                            <Styled.NarrowCell>{t("life.contractDetail.coverags.table.exclusion")}</Styled.NarrowCell>
                            <Styled.NarrowCell>
                                {!isNil(coverage.exclusion) ? t("global.yes") : t("global.no")}
                            </Styled.NarrowCell>
                        </TableRow>
                    </Styled.InsuranceCoverageMobileTableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};
